"use client";

import React from "react";
import { Spinner, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { DOCXViewer } from "@webuild-ai/react-ui/components";

import {
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalActionButton,
} from "@/components/UI/ModalOverlay";
import { extractSafeDownloadUrl, isValidOfficeViewerUrl } from "@/utils/urlValidation";
import { ExcelViewer } from "@/components/Citations/viewers/ExcelViewer";
import { PPTXViewer } from "@/components/Citations/viewers/PPTXViewer";
import { PDFViewer } from "@/components/Citations/viewers/PDFViewer";
import { DocumentType, detectDocumentTypeFromMimeAndFilename } from "@/utils/fileUtils";
import { logger } from "@/telemetry";

export type FilePreviewModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fileName?: string;
  fileType?: string;
  fileUrl?: string | null;
  /** Document data as ArrayBuffer for client-side Office rendering (DOCX, XLSX, PPTX) */
  documentData?: ArrayBuffer | null;
  isLoading?: boolean;
  error?: string | null;
  onDownload?: () => void | Promise<void>;
};

type ToastType = "loading" | "success" | "error";

interface DownloadToastState {
  visible: boolean;
  type: ToastType;
  message: string;
}

const HIDDEN_TOAST: DownloadToastState = { visible: false, type: "loading", message: "" };

/** Matches an image file extension followed by a query string, a fragment or the end. */
const IMAGE_EXTENSION_PATTERN = /(\.(png|jpe?g|gif|webp|svg))(\?|#|$)/i;

const canEmbedFileType = (fileType: string | undefined): boolean => {
  if (!fileType) return true; // try embed by default
  const lower = fileType.toLowerCase();

  return (
    lower.includes("pdf") ||
    lower.startsWith("image/") ||
    lower === "text/plain" ||
    lower === "text/markdown" ||
    lower === "text/html"
  );
};

const isImageSource = (
  fileType: string | undefined,
  fileUrl: string | null | undefined,
): boolean => {
  const type = (fileType || "").toLowerCase();

  if (type.startsWith("image/")) return true;
  if (!type && fileUrl) {
    // fallback by extension if type is unknown
    return IMAGE_EXTENSION_PATTERN.test(fileUrl);
  }

  return false;
};

const PreviewLoading: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3" role="status">
      <Spinner />
      <p className="text-sm text-default-500">{t("Loading preview...")}</p>
    </div>
  );
};

interface PreviewErrorProps {
  error: string;
}

const PreviewError: React.FC<PreviewErrorProps> = ({ error }) => (
  <div className="p-4 text-danger">{error}</div>
);

interface DocumentPreviewProps {
  documentType: DocumentType;
  documentData: ArrayBuffer;
  fileName?: string;
}

/**
 * Client-side document rendering (PDF + Office).
 * Each viewer owns its own scrollable container (h-[75vh] overflow-auto);
 * the outer ModalBody is overflow-hidden so we never stack scrollbars.
 */
const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  documentType,
  documentData,
  fileName,
}) => {
  const { t } = useTranslation();

  if (documentType === "pdf") {
    return <PDFViewer documentData={documentData} />;
  }
  if (documentType === "docx") {
    return (
      <DOCXViewer
        documentData={documentData}
        onError={(err) => logger.error("Failed to render DOCX", err)}
      />
    );
  }
  if (documentType === "excel") {
    return <ExcelViewer documentData={documentData} filename={fileName} />;
  }
  if (documentType === "pptx") {
    return <PPTXViewer documentData={documentData} />;
  }

  return <div className="p-4 text-sm text-default-600">{t("filePreview.unsupportedType")}</div>;
};

interface UrlPreviewProps {
  fileUrl: string;
  fileName?: string;
  embedUrl?: string | null;
  isImage: boolean;
  canEmbed: boolean;
}

const UrlPreview: React.FC<UrlPreviewProps> = ({
  fileUrl,
  fileName,
  embedUrl,
  isImage,
  canEmbed,
}) => {
  if (isImage) {
    return (
      <div className="w-full h-full flex items-center justify-center overflow-auto">
        <img
          alt={fileName || "image-preview"}
          className="max-h-full max-w-full object-contain"
          src={fileUrl}
          style={{ display: "block" }}
        />
      </div>
    );
  }
  if (isValidOfficeViewerUrl(fileUrl)) {
    return (
      <iframe
        className="w-full h-full"
        sandbox="allow-same-origin allow-scripts allow-popups"
        src={fileUrl}
        style={{ border: "none" }}
        title={fileName || "file-preview"}
      />
    );
  }
  if (canEmbed) {
    // Sandbox added for defence-in-depth. Reached for embeddable file types
    // (PDF, text/plain, text/markdown, text/html) and unknown MIME types.
    // Images are handled above via <img>. allow-same-origin needed for PDF.js
    // rendering; allow-scripts needed for interactive content. See PWI-194.
    return (
      <iframe
        className="w-full h-full"
        sandbox="allow-same-origin allow-scripts"
        src={embedUrl || fileUrl}
        style={{ border: "none" }}
        title={fileName || "file-preview"}
      />
    );
  }

  return (
    <div className="p-4 text-sm text-default-600">This file type cannot be previewed here.</div>
  );
};

interface PreviewContentProps {
  isLoading?: boolean;
  error?: string | null;
  documentData?: ArrayBuffer | null;
  documentType: DocumentType | null;
  fileName?: string;
  fileUrl?: string | null;
  embedUrl?: string | null;
  isImage: boolean;
  canEmbed: boolean;
}

const PreviewContent: React.FC<PreviewContentProps> = ({
  isLoading,
  error,
  documentData,
  documentType,
  fileName,
  fileUrl,
  embedUrl,
  isImage,
  canEmbed,
}) => {
  if (isLoading) return <PreviewLoading />;
  if (error) return <PreviewError error={error} />;
  if (documentData && documentType) {
    return (
      <DocumentPreview
        documentData={documentData}
        documentType={documentType}
        fileName={fileName}
      />
    );
  }
  if (fileUrl) {
    return (
      <UrlPreview
        canEmbed={canEmbed}
        embedUrl={embedUrl}
        fileName={fileName}
        fileUrl={fileUrl}
        isImage={isImage}
      />
    );
  }

  return <div className="p-4 text-sm text-default-600">No preview available.</div>;
};

interface DownloadToastProps {
  toast: DownloadToastState;
}

const DownloadToast: React.FC<DownloadToastProps> = ({ toast }) => (
  <div
    className={cn(
      "flex items-center gap-2 px-3 py-1.5 rounded-medium text-tiny",
      // Bare `success` and `danger` rather than a numbered stop. The compat
      // layer maps those two families only at 50, 100 and 200, so the 700
      // stops compiled to nothing and both toast texts inherited the ambient
      // colour. `default` does map to 700, so that branch is left alone.
      toast.type === "success"
        ? "bg-success-100 text-success"
        : toast.type === "error"
          ? "bg-danger-100 text-danger"
          : "bg-default-100 text-default-700",
    )}
  >
    {toast.type === "loading" ? (
      <Spinner size="sm" />
    ) : (
      <Icon
        icon={toast.type === "success" ? "solar:check-circle-linear" : "solar:danger-circle-linear"}
        width={16}
      />
    )}
    <span>{toast.message}</span>
  </div>
);

interface DownloadActionProps {
  onDownload?: () => void | Promise<void>;
  onDownloadPress: () => void;
  isDownloading: boolean;
  downloadHref?: string;
  fileName?: string;
}

const DownloadAction: React.FC<DownloadActionProps> = ({
  onDownload,
  onDownloadPress,
  isDownloading,
  downloadHref,
  fileName,
}) => {
  const { t } = useTranslation();

  if (onDownload) {
    return (
      <ModalActionButton
        aria-label={t("Download")}
        className="min-w-30"
        isDisabled={isDownloading}
        isPending={isDownloading}
        size="sm"
        onPress={onDownloadPress}
      >
        {!isDownloading && <Icon icon="solar:download-bold" width={18} />}{" "}
        {isDownloading ? t("Downloading...") : t("Download")}
      </ModalActionButton>
    );
  }
  if (downloadHref) {
    return (
      <ModalActionButton
        aria-label={t("Download")}
        as="a"
        className="min-w-30"
        download={fileName || "download"}
        href={downloadHref}
        size="sm"
      >
        <Icon icon="solar:download-bold" width={18} /> {t("Download")}
      </ModalActionButton>
    );
  }

  return null;
};

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onOpenChange,
  fileName,
  fileType,
  fileUrl,
  documentData,
  isLoading,
  error,
  onDownload,
}) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [toast, setToast] = React.useState<DownloadToastState>(HIDDEN_TOAST);

  const canEmbed = React.useMemo(() => canEmbedFileType(fileType), [fileType]);

  const isImage = React.useMemo(() => isImageSource(fileType, fileUrl), [fileType, fileUrl]);

  // Detect document types for client-side rendering (Office + PDF)
  // Uses centralised utility for consistent detection across the application
  const documentType = React.useMemo((): DocumentType | null => {
    return detectDocumentTypeFromMimeAndFilename(fileType, fileName);
  }, [fileType, fileName]);

  // Passed through unmodified for the embeddable non-document types below.
  // NOTE: this no longer carries PDFs. `documentType === "pdf"` intercepts every
  // PDF above and renders it through the client-side PDFViewer, so the browser's
  // native PDF toolbar (and with it zoom in and zoom out) is not reachable here.
  // See the pdfjs-dist section of CLAUDE.md before attributing a PDF sizing or
  // zoom report to a dependency upgrade.
  const embedUrl = React.useMemo(() => {
    return fileUrl;
  }, [fileUrl]);

  const downloadHref = React.useMemo(() => {
    return extractSafeDownloadUrl(fileUrl);
  }, [fileUrl]);

  // toast helpers
  const toastTimerRef = React.useRef<number | null>(null);
  const showToast = React.useCallback((type: ToastType, message: string, autoHideMs?: number) => {
    setToast({ visible: true, type, message });
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    if (autoHideMs) {
      toastTimerRef.current = window.setTimeout(() => {
        setToast((prev) => ({ ...prev, visible: false }));
        toastTimerRef.current = null;
      }, autoHideMs);
    }
  }, []);

  const handleDownloadPress = React.useCallback(async () => {
    if (!onDownload || isDownloading) return;
    try {
      setIsDownloading(true);
      showToast("loading", t("Downloading...") || "Downloading...");
      await onDownload();
      showToast("success", t("Download complete") || "Download complete", 2000);
    } catch (e) {
      logger.error("Download error", e);
      showToast("error", t("Download failed") || "Download failed", 3000);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownload, isDownloading, showToast, t]);

  React.useEffect(() => {
    if (!isOpen) {
      setIsDownloading(false);
      setToast(HIDDEN_TOAST);
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} scrollBehavior="inside" size="4xl" onOpenChange={onOpenChange}>
      <ModalHeader className="flex items-center gap-2 border-b border-divider">
        <Icon className="text-brand-primary" icon="solar:document-linear" width={20} />
        <span className="truncate" title={fileName}>
          {fileName || t("Preview")}
        </span>
      </ModalHeader>
      <ModalBody className="p-0 flex-1 min-h-0 overflow-hidden bg-content1">
        <PreviewContent
          canEmbed={canEmbed}
          documentData={documentData}
          documentType={documentType}
          embedUrl={embedUrl}
          error={error}
          fileName={fileName}
          fileUrl={fileUrl}
          isImage={isImage}
          isLoading={isLoading}
        />
      </ModalBody>
      <ModalFooter className="justify-end gap-3">
        {toast.visible && <DownloadToast toast={toast} />}
        <DownloadAction
          downloadHref={downloadHref}
          fileName={fileName}
          isDownloading={isDownloading}
          onDownload={onDownload}
          onDownloadPress={handleDownloadPress}
        />
      </ModalFooter>
    </ModalOverlay>
  );
};

export default FilePreviewModal;
